import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntersection } from 'react-use';
import colors from '@c2fo/liquidity/colors';
import { ChartLineIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import { useReporting } from '@/reporting';
import useLocaleFormat from '@/utils/useLocaleFormat';
import Skeleton from '../../components/Skeleton';
import { createFiqUrl } from './createFiqUrl';
import { FinanceIQBreakdownModal } from './FinanceIQBreakdownModal';
import FinanceIQChart from './FinanceIQChart';
import FinanceIQKpiCard from './FinanceIQKpiCard';
import { useBuyerCashflow } from './useBuyerCashflow';
import { pastDueFromDate, pastDueQueryParams, useDynamicStats } from './useDynamicStats';
import { useFinanceIQCustomerNames } from './useFinanceIQCustomerNames';
import type { FinanceIQBreakdownKpiType } from './FinanceIQBreakdownModal';
import type { BuyerCashflowData } from './useBuyerCashflow';

// All date constants are derived from UTC so the same calendar date is used regardless
// of the viewer's local timezone. new Date().toISOString() always returns the UTC date,
// and Date.UTC arithmetic keeps everything in UTC throughout.
const now = new Date();
const todayYear = now.getUTCFullYear();
const todayMonth = now.getUTCMonth() + 1; // 1-based
const todayDay = now.getUTCDate();
const mm = String(todayMonth).padStart(2, '0');
const yyyy = String(todayYear);
const START_DATE = `${yyyy}-${mm}-01`;
// Date.UTC(year, month, 0) = last day of the previous month in UTC, i.e. last day of todayMonth
const END_DATE_LAST_DAY = new Date(Date.UTC(todayYear, todayMonth, 0)).getUTCDate();
const END_DATE = `${yyyy}-${mm}-${String(END_DATE_LAST_DAY).padStart(2, '0')}`;
const TODAY_STRING = `${yyyy}-${mm}-${String(todayDay).padStart(2, '0')}`;
// Date objects for display — passed as Date so asDateStringText skips the bare-string local-time
// parsing path in asDateString, ensuring UTC midnight is used regardless of the viewer's timezone.
const START_DATE_OBJ = new Date(Date.UTC(todayYear, todayMonth - 1, 1));
const END_DATE_OBJ = new Date(Date.UTC(todayYear, todayMonth - 1, END_DATE_LAST_DAY));

// A customer row with every metric zeroed. Callers spread in only the fields they have.
const EMPTY_CUSTOMER: BuyerCashflowData['byCustomer'][number] = {
  makerOrganizationUuid: '',
  totalInvoiceAmount: 0,
  totalInvoiceCount: 0,
  totalDeductionAmount: 0,
  totalDeductionCount: 0,
  futureDueAmount: 0,
  futureDueCount: 0,
  pastDueAmount: 0,
  pastDueCount: 0,
  paidOnTermsCount: 0,
  paidOnTermsAmount: 0,
  c2foEarlyPaidCount: 0,
  c2foEarlyPaidAmount: 0,
  c2foEarlyPaidDiscount: 0,
  incomingCashCount: 0,
  incomingCashAmount: 0,
  collectedCount: 0,
  collectedAmount: 0,
  remainingCount: 0,
  remainingAmount: 0,
};

const BAR_HEIGHTS = [
  40, 55, 35, 60, 45, 70, 50, 80, 55, 65, 45, 75, 60, 85, 50, 40, 70, 55, 65, 45, 80, 60, 50, 70, 55, 65, 45, 60,
];

const ChartSkeleton = () => (
  <div className="flex h-[280px] items-end gap-2 px-8 pb-8 pt-4">
    {BAR_HEIGHTS.map((h, i) => (
      <div key={i} className="flex-1 animate-pulse rounded-sm bg-gray-200" style={{ height: `${h}%` }} />
    ))}
  </div>
);

const KpiCardSkeleton = () => (
  <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
    <Skeleton className="h-4 w-24" shadow={false} />
    <Skeleton className="h-9 w-32" shadow={false} />
    <Skeleton className="h-4 w-20" shadow={false} />
  </div>
);

const FinanceIQWidgetContent = () => {
  const { t } = useTranslation();
  const { asNumber, asDateStringText, asCurrencyCompact } = useLocaleFormat();
  const { trackEnterpriseEvent } = useReporting();
  const { selectedCurrency } = useCurrencyContext();

  const { data, isLoading } = useBuyerCashflow({
    effectiveDueDateFrom: START_DATE,
    effectiveDueDateTo: END_DATE,
    targetCurrency: selectedCurrency,
  });

  const { data: pastDueData, isLoading: isPastDueLoading } = useDynamicStats({
    ...pastDueQueryParams,
    targetCurrency: selectedCurrency,
  });

  // Map the dynamic-stats per-customer shape ({ makerOrganizationUuid, count, amount }) onto the
  // cashflow customer shape the breakdown modal consumes, populating only the past-due fields.
  const pastDueByCustomer = useMemo<BuyerCashflowData['byCustomer']>(
    () =>
      (pastDueData?.byCustomer ?? []).map((c) => ({
        ...EMPTY_CUSTOMER,
        makerOrganizationUuid: c.makerOrganizationUuid,
        pastDueAmount: c.amount,
        pastDueCount: c.count,
      })),
    [pastDueData]
  );

  const customerNames = useFinanceIQCustomerNames();
  // FinanceIQChart expects a flat UUID → name map for its tooltip
  const customerNameStrings = useMemo(
    () => Object.fromEntries(Object.entries(customerNames).map(([uuid, info]) => [uuid, info.displayName])),
    [customerNames]
  );
  const [breakdownKpi, setBreakdownKpi] = useState<FinanceIQBreakdownKpiType | null>(null);
  const [chartBreakdown, setChartBreakdown] = useState<{
    periodStart: string;
    byCustomer: BuyerCashflowData['byCustomer'];
    currency: string;
  } | null>(null);

  const { totals, currency, byPeriod } = data ?? {
    totals: null,
    currency: 'USD',
    byPeriod: { byDay: [] },
  };

  const incomingAmount = totals ? totals.incomingCashAmount : 0;
  const incomingCount = totals ? totals.incomingCashCount : 0;

  const deductionAmount = totals ? -Math.abs(totals.totalDeductionAmount) : 0;
  const deductionCount = totals ? totals.totalDeductionCount : 0;
  const pastDueAmount = pastDueData ? pastDueData.totals.amount : 0;
  const pastDueCount = pastDueData ? pastDueData.totals.count : 0;
  const pastDueCurrency = pastDueData?.currency ?? currency;

  // Empty when data failed to load or every value is zero — error and no-data share the same empty state
  const hasKpiData = !!data && (incomingCount > 0 || deductionCount > 0);
  const hasPastDueData = !!pastDueData && pastDueCount > 0;

  const startDateDisplay = asDateStringText({ date: START_DATE_OBJ, isUTC: true });
  const endDateDisplay = asDateStringText({ date: END_DATE_OBJ, isUTC: true });
  const discountAmount = asCurrencyCompact(data ? Math.abs(data.totals.c2foEarlyPaidDiscount) : 0, currency);

  // Track once on mount regardless of scroll position
  useEffect(() => {
    trackEnterpriseEvent('financeiq-widget::mounted', { currency });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track once when the widget first scrolls into view
  const widgetRef = useRef<HTMLDivElement>(null);
  // react-use's types still expect RefObject<HTMLElement> (non-null); React 19's useRef(null) is correctly RefObject<HTMLElement | null>
  const intersection = useIntersection(widgetRef as RefObject<HTMLDivElement>, { threshold: 0.1 });
  const hasTrackedViewed = useRef(false);

  useEffect(() => {
    if (intersection?.isIntersecting && !hasTrackedViewed.current) {
      hasTrackedViewed.current = true;
      trackEnterpriseEvent('financeiq-widget::viewed', { currency });
    }
  }, [intersection?.isIntersecting, currency, trackEnterpriseEvent]);

  const handleKpiCardClick = (kpiType: FinanceIQBreakdownKpiType) => {
    setBreakdownKpi(kpiType);
    const customerCount = kpiType === 'past_due' ? pastDueData?.byCustomer.length ?? 0 : data?.byCustomer.length ?? 0;
    trackEnterpriseEvent('financeiq-widget::kpi-card::clicked', { kpiType, currency, customerCount });
  };

  const handleChartBarClick = (periodStart: string, byCustomer: BuyerCashflowData['byCustomer'], curr: string) => {
    setChartBreakdown({ periodStart, byCustomer, currency: curr });
    trackEnterpriseEvent('financeiq-widget::chart-bar::clicked', {
      periodStart,
      currency: curr,
      customerCount: byCustomer.length,
    });
  };

  return (
    <div ref={widgetRef}>
      {/* Page-level header — sits above the white card */}
      <div className="mb-4 flex flex-col items-start gap-1">
        <div className="flex items-center gap-2">
          <ChartLineIcon className="h-6 w-6 fill-gray-900" />
          <h2 className="m-0 p-0 text-xl font-semibold text-gray-900">{t('financeiq.widget.title')}</h2>
        </div>
        <p className="m-0 p-0 text-sm text-gray-500">{t('financeiq.widget.subheading')}</p>
      </div>

      {/* White card */}
      <div className="rounded-2xl bg-white p-6 shadow-md">
        {/* Financial Health section */}
        <div className="mb-6">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="m-0 p-0 text-lg font-semibold text-gray-900">{t('financeiq.widget.financialHealth')}</h3>
              <p className="m-0 p-0 text-sm text-gray-500">
                {t('financeiq.widget.dateRange', { startDate: startDateDisplay, endDate: endDateDisplay })}
              </p>
            </div>
            <a
              href={createFiqUrl('/', { preferredCurrency: currency })}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 text-sm font-semibold text-blue-500"
              onClick={() => trackEnterpriseEvent('financeiq-widget::dashboard-link::clicked', { currency })}
            >
              {t('financeiq.widget.dashboardLink')} →
            </a>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {isLoading ? (
              <>
                <KpiCardSkeleton />
                <KpiCardSkeleton />
              </>
            ) : (
              <>
                <FinanceIQKpiCard
                  title={t('financeiq.kpi.incomingCash.title')}
                  titleTooltip={t('financeiq.kpi.incomingCash.tooltip', { discountAmount })}
                  amount={asCurrencyCompact(incomingAmount, currency)}
                  count={t('core.invoiceWithCount', {
                    count: incomingCount,
                    formattedCount: asNumber({ value: incomingCount }),
                  })}
                  accentColor={colors.primary[500]}
                  isEmpty={!hasKpiData}
                  onClick={() => handleKpiCardClick('incoming_cash')}
                />
                <FinanceIQKpiCard
                  title={t('financeiq.kpi.deductions.title')}
                  amount={asCurrencyCompact(deductionAmount, currency)}
                  count={t('core.deductionWithCount', {
                    count: deductionCount,
                    formattedCount: asNumber({ value: deductionCount }),
                  })}
                  accentColor={colors.orange[500]}
                  isEmpty={!hasKpiData}
                  onClick={() => handleKpiCardClick('deductions')}
                />
              </>
            )}
            {isPastDueLoading ? (
              <KpiCardSkeleton />
            ) : (
              <FinanceIQKpiCard
                title={t('financeiq.kpi.pastDue.title')}
                amount={asCurrencyCompact(pastDueAmount, pastDueCurrency)}
                count={t('financeiq.kpi.pastDue.subtext', { invoiceCount: asNumber({ value: pastDueCount }) })}
                accentColor={colors.red[500]}
                isEmpty={!hasPastDueData}
                onClick={() => handleKpiCardClick('past_due')}
              />
            )}
          </div>
        </div>

        {/* Cash Flow section */}
        <div>
          <div className="mb-0 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">{t('financeiq.widget.cashFlow')}</h3>
            <a
              href={createFiqUrl('/cashflow-manager', { preferredCurrency: currency })}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-semibold text-blue-500 hover:underline"
              onClick={() => trackEnterpriseEvent('financeiq-widget::cashflow-manager-link::clicked', { currency })}
            >
              {t('financeiq.widget.cashFlowManagerLink')} →
            </a>
          </div>
          {isLoading ? (
            <ChartSkeleton />
          ) : (
            <FinanceIQChart
              periods={hasKpiData ? byPeriod.byDay : []}
              currency={currency}
              todayString={TODAY_STRING}
              customerNames={customerNameStrings}
              onBarClick={handleChartBarClick}
            />
          )}
        </div>
      </div>

      {breakdownKpi && (
        <FinanceIQBreakdownModal
          isOpen
          onClose={() => setBreakdownKpi(null)}
          kpiType={breakdownKpi}
          title={
            breakdownKpi === 'incoming_cash'
              ? t('financeiq.kpi.incomingCash.title')
              : breakdownKpi === 'deductions'
              ? t('financeiq.kpi.deductions.title')
              : t('financeiq.kpi.pastDue.title')
          }
          effectivePayDateFrom={breakdownKpi === 'past_due' ? undefined : START_DATE}
          effectivePayDateTo={breakdownKpi === 'past_due' ? undefined : END_DATE}
          transactionDateFrom={breakdownKpi === 'past_due' ? pastDueFromDate : undefined}
          transactionDateTo={breakdownKpi === 'past_due' ? TODAY_STRING : undefined}
          data={{
            byCustomer: breakdownKpi === 'past_due' ? pastDueByCustomer : data?.byCustomer ?? [],
            currency: breakdownKpi === 'past_due' ? pastDueCurrency : currency,
          }}
          customerNames={customerNames}
        />
      )}

      {chartBreakdown && (
        <FinanceIQBreakdownModal
          isOpen
          onClose={() => setChartBreakdown(null)}
          kpiType="chart_period"
          title={t('financeiq.widget.cashFlow')}
          effectivePayDateFrom={chartBreakdown.periodStart}
          effectivePayDateTo={chartBreakdown.periodStart}
          data={{ byCustomer: chartBreakdown.byCustomer, currency: chartBreakdown.currency }}
          customerNames={customerNames}
        />
      )}
    </div>
  );
};

export default FinanceIQWidgetContent;
